<template>
  <Drawer
    :visible="visible"
    position="right"
    :showCloseIcon="false"
    aria-label="Table Layout Settings"
    @update:visible="onVisibleChange"
  >
    <h3>{{ t('labels.tableSettingsHdlSize') }}</h3>
    <div class="field-select">
      <label class="hidden">{{ t('labels.tableSettingsFontsize') }}</label>
      <SelectButton
        v-model="selectedSizeValue"
        :options="sizeOptions"
        :allowEmpty="false"
        optionLabel="label"
        optionValue="value"
        data-key="value"
      />
    </div>

    <h3>{{ t('labels.tableSettingsHdlColumns') }}</h3>
    <div v-for="option in sortedColumnOptions" :key="option.field" class="field-checkbox">
      <Checkbox v-model="selectedColumns" :value="option.field" @change="emitColumnChange" />
      <label>{{ t(`labels.${option.label}`) }}</label>
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import Checkbox from 'primevue/checkbox';
import Drawer from 'primevue/drawer';
import SelectButton from 'primevue/selectbutton';
import { useI18n } from 'vue-i18n';

import { useSizeStore } from '@/stores/tableSize';

import type { PaginatedDataTableColumnConfig } from '../types/paginatedDataTableTypes';

const { t } = useI18n();

const props = defineProps<{
  columns: PaginatedDataTableColumnConfig[];
  visibleColumns: string[];
  visible: boolean;
  sizeOptions?: { label: string; value: string }[];
  dataKey?: string;
}>();

const columnOptions = computed(() => {
  return props.columns
    .filter((col) => !col.hidden)
    .map((col) => ({
      label: col.title || col.header || col.field,
      field: col.field,
    }));
});

const sizeStore = useSizeStore();
const selectedColumns = ref([...props.visibleColumns]);

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'update-settings', payload: { key: string; value: boolean }): void;
  (e: 'update:visibleColumns', value: string[]): void;
  (e: 'update:size', value: string): void;
}>();

const sizeOptions = computed(
  () =>
    props.sizeOptions ?? [
      { label: t('buttons.small'), value: 'small' },
      { label: t('buttons.normal'), value: 'normal' },
      { label: t('buttons.large'), value: 'large' },
    ],
);

const selectedSizeValue = ref(sizeStore.size);

const sortedColumnOptions = computed(() => {
  const selectedFieldsSet = new Set(selectedColumns.value);
  const allColumnOptions = columnOptions.value;

  // Sort the selected and remaining columns
  const selectedOptions = selectedColumns.value
    .map((selectedField) => allColumnOptions.find((option) => option.field === selectedField))
    .filter((option) => option !== undefined) as Array<{ label: string; field: string }>;

  const remainingOptions = allColumnOptions.filter(
    (option) => !selectedFieldsSet.has(option.field),
  );

  const allSortingOptions = [...selectedOptions, ...remainingOptions];

  return allSortingOptions;
});

const emitColumnChange = () => {
  emit('update:visibleColumns', selectedColumns.value);
};

watch(selectedSizeValue, (newSize) => {
  sizeStore.setSize(newSize);
  emit('update:size', newSize);
});

watch(
  () => props.visibleColumns, // Watch for changes to `props.visibleColumns`
  (newVisibleColumns) => {
    selectedColumns.value = [...newVisibleColumns]; // Update `selectedColumns` with new values
  },
  { immediate: true }, // Execute immediately when component is mounted
);

// Handle visibility changes - hide on overlay click
const onVisibleChange = (newValue: boolean) => {
  emit('update:visible', newValue);
};

onMounted(() => {
  selectedColumns.value = [...props.visibleColumns];
  selectedSizeValue.value = sizeStore.size;
});
</script>

<style scoped lang="css">
@reference "tailwindcss";
@reference "tailwindcss/utilities";

@layer app {
  .field-checkbox {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
  }

  .field-checkbox label {
    margin-left: 0.5rem;
  }

  .field-select {
    /* display: flex; */
    align-items: center;
    margin-bottom: 1rem;
  }

  .field-select label {
    margin-left: 0.5rem;
    @apply mb-2 block;
  }

  h3 {
    @apply mt-8;
  }
}
</style>
